<template>
  <div class="flex flex-wrap items-start mt-5 w-full">
    <EmailField
      ref="email"
      v-model="fields.email"
      class="w-full field mb-4"
      label="Billing Email"
      :error="errors.email"
      :readonly="true"
      :disabled="true"
    />
    <div class="w-full flex flex-wrap lg:flex-no-wrap space-x-0 lg:space-x-8">
      <NameField
        ref="firstName"
        v-model="fields.first_name"
        class="field w-full lg:w-1/2"
        label="First Name"
        :error="errors.first_name"
        @onFocus="onFocus('first_name', 'First Name')"
        @onBlur="onBlur('first_name', 'First Name')"
      />
      <NameField
        ref="lastName"
        v-model="fields.last_name"
        class="field w-full lg:w-1/2"
        label="Last Name"
        :error="errors.last_name"
        @onFocus="onFocus('last_name', 'Last Name')"
        @onBlur="onBlur('last_name', 'Last Name')"
      />
    </div>
    <div
      class="w-full flex flex-wrap"
      :class="isShowAddressAutocompleteFields ? 'lg:flex-no-wrap space-x-0 lg:space-x-8' : 'lg:flex-wrap'"
    >
      <FloatingInput
        ref="company"
        v-model="fields.company"
        type="text"
        label="Company name"
        class="field w-full lg:w-1/2"
        :class="!isShowAddressAutocompleteFields && 'lg:pr-4'"
        :error="errors.company"
        @onFocus="onFocus('company', 'Company name')"
        @onBlur="onBlur('company', 'Company name')"
      />
      <VatIdField
        v-if="!isShowAddressAutocompleteFields"
        ref="vatId"
        v-model="fields.vat_id"
        class="field w-full lg:w-1/2"
        :class="!isShowAddressAutocompleteFields && 'lg:pl-4'"
        :error="errors.vat_id || errors.tax_id"
        :country="fields.country"
        @onFocus="onFocus('vat_id', showAbn ? 'ABN' : 'Vat ID')"
        @onBlur="onBlur('vat_id', showAbn ? 'ABN' : 'Vat ID')"
      />
      <FloatingInput
        ref="phoneNumber"
        v-model="fields.phone_number"
        type="tel"
        label="Phone Number"
        class="field w-full lg:w-1/2"
        :class="!isShowAddressAutocompleteFields && 'lg:pr-4'"
        :error="errors.phone_number"
        @onFocus="onFocus('phone_number', 'Phone Number')"
        @onBlur="onBlur('phone_number', 'Phone Number')"
      />
    </div>
    <div
      v-if="countryLoading"
      class="inline-flex items-center justify-center ml-4"
    >
      <div class="w-4 h-4 border-b-2 rounded-full border-ma animate-spin" />
    </div>
    <CountryField
      v-else
      ref="country"
      v-model="fields.country"
      class="w-full field"
      :is-default-country="false"
      :country-id="countryId"
      :error="errors.country"
      label="*Country"
      @onFocus="onFocus('country', 'country')"
      @onBlur="onBlur('country', 'country')"
    />
    <div
      v-show="isShowAddressAutocompleteFields"
      class="w-full"
    >
      <AutocompleteAddress
        ref="userAddress"
        v-model="fields.user_address"
        label="*Address"
        :error="errors.user_address"
        @onFocus="onFocus('user_address', 'address')"
        @onBlur="onBlur('user_address', 'address')"
      />
      <div
        v-if="statesList.length > 0"
        class="w-full flex flex-wrap lg:flex-no-wrap space-x-0 lg:space-x-8"
      >
        <AutocompleteCity
          ref="userCity"
          v-model="fields.user_city"
          class="field left-half"
          :error="errors.user_city"
          @onFocus="onFocus('user_city', 'city')"
          @onBlur="onBlur('user_city', 'city')"
        />
        <AutocompleteState
          ref="userState"
          v-model="fields.user_state"
          class="field right-half"
          :states="statesList"
          :error="errors.user_state"
          @onFocus="onFocus('user_state', 'state')"
          @onBlur="onBlur('user_state', 'state')"
        />
      </div>
    </div>
    <ZipCodeField
      v-show="showZipField"
      ref="zip"
      v-model="fields.zip"
      class="w-full field"
      :country="fields.country"
      label="Zip / Postal Code"
      :error="errors.zip"
      @onFocus="onFocus('zip', 'Zip / Postal Code')"
      @onBlur="onBlur('zip', 'Zip / Postal Code')"
    />
  </div>
</template>

<script>
import EmailField from '@/components/Fields/email';
import NameField from '@/components/Fields/name.vue';
import FloatingInput from '@/components/FloatingInput';
import CountryField from '@/components/Fields/country.vue';
import AutocompleteAddress from '@/components/Fields/autocomplete/address.vue';
import AutocompleteCity from '@/components/Fields/autocomplete/city.vue';
import AutocompleteState from '@/components/Fields/autocomplete/state.vue';
import ZipCodeField from '@/components/Fields/zipcode.vue';
import { getCountriesList } from '@/services/countries-api.js';
import VatIdField from '@/components/Fields/vat-id.vue';
import analyticsEvents from '@/utils/analyticsEvents';
import { eventTracker } from '@/utils/eventTracker';

// mixins
import EmailFieldValidation from '@/mixins/form-validation/EmailFieldValidation.vue';
import FirstNameFieldValidation from '@/mixins/form-validation/FirstNameFieldValidation.vue';
import LastNameFieldValidation from '@/mixins/form-validation/LastNameFieldValidation.vue';
import ZipFieldValidation from '@/mixins/form-validation/ZipFieldValidation.vue';
import AddressFieldValidation from '@/mixins/form-validation/AddressFieldValidation.vue';
import CityFieldValidation from '@/mixins/form-validation/CityFieldValidation.vue';
import StateFieldValidation from '@/mixins/form-validation/StateFieldValidation.vue';
import CountryValidation from '@/mixins/form-validation/CountryValidation.vue';
import VatIdFieldValidation from '@/mixins/form-validation/VatIdFieldValidation.vue';
import PopulateAddress from '@/mixins/PopulateAddress.vue';

export default {
  components: {
    EmailField,
    NameField,
    FloatingInput,
    CountryField,
    AutocompleteAddress,
    AutocompleteCity,
    AutocompleteState,
    ZipCodeField,
    VatIdField,
  },
  mixins: [
    AddressFieldValidation,
    CityFieldValidation,
    EmailFieldValidation,
    FirstNameFieldValidation,
    LastNameFieldValidation,
    StateFieldValidation,
    ZipFieldValidation,
    PopulateAddress,
    CountryValidation,
    VatIdFieldValidation,
  ],
  props: {
    team: {
      type: Object,
      default: () => ({}),
      required: true,
    },
  },
  data() {
    return {
      errors: {},
      lastValidationStatus: null,
      fields: {
        id: '',
        email: '',
        first_name: '',
        last_name: '',
        company: '',
        user_address: '',
        user_city: '',
        user_state: {},
        zip: '',
        phone_number: '',
        seats: {},
        vat_id: '',
        team_memberships: [],
        country: {
          name: this.team.country || '',
          id: this.team?.country_id || null,
        },
      },
      addressLastValidationStatus: null,
      addressPopulated: false,
      currentCountry: {},
      countryLoading: false,
      fieldsPreviousValue: {},
    };
  },
  computed: {
    autocompleteInstance() {
      return this.$store.getters.autocompleteInstance;
    },
    countryUsa() {
      return this.$store.getters.countryUsa;
    },
    isShowAddressAutocompleteFields() {
      return this.fields.country?.id === this.countryUsa.id;
    },
    statesList() {
      return this.countryUsa?.states || [];
    },
    getState() {
      if (this.countryUsa.id === this.fields.country?.id) {
        const stateName = this.team.state?.name || this.team.state || '';
        const currentState = this.fields.country.states.find(state => state.name === stateName);
        return currentState;
      }
      return {};
    },
    countryId() {
      return this.fields.country?.id;
    },
    isFormValid() {
      const hasFirstName = this.$refs.firstName?.validate;
      const hasLastName = this.$refs.lastName?.validate;
      const hasEmail = this.$refs.email?.validate;
      const hasABN = this.showABN && this.$refs.vatId?.value ? this.$refs.vatId?.validate : true;
      return hasFirstName && hasLastName && hasEmail && hasABN && this.isAddressValid;
    },
    isAddressValid() {
      const isUSA = this.fields.country?.id === this.countryUsa?.id;

      const hasCountry = this.$refs.country?.validate;
      const hasZipCode = this.$refs.zip?.validate;

      const hasAddressIfUSA = isUSA ? this.$refs.userAddress?.validate : true;
      const hasCityIfUSA = isUSA ? this.$refs.userCity?.validate : true;
      const hasStateIfUSA = isUSA ? this.$refs.userState?.validate : true;
      return hasCountry && hasZipCode && hasAddressIfUSA && hasCityIfUSA && hasStateIfUSA;
    },
    isUSA() {
      return this.fields.country?.id === this.countryUsa?.id;
    },
    showABN() {
      return this.fields.country?.id === this.$store.state.subscription.countryAustralia?.id;
    },
    showZipField() {
      // for now, only show zip field for US
      return this.isUSA;
    },
  },
  watch: {
    fields: {
      handler() {
        this.emitValidateStatus();
      },
      deep: true,
    },
    team() {
      const {
        billing_email,
        billing_first_name,
        billing_last_name,
        company_name,
        address,
        city,
        state,
        postal_code,
        country_id,
        tax_id,
        ...restFields
      } = this.team;

      this.countryLoading = country_id !== null;

      this.fields = {
        ...restFields,
        email: billing_email || '',
        first_name: billing_first_name || '',
        last_name: billing_last_name || '',
        company: company_name || '',
        user_address: address || '',
        user_city: city || '',
        user_state: {
          name: state || '',
        },
        zip: postal_code || '',
        country: {
          id: country_id || null,
        },
        vat_id: tax_id || '',
      };
      this.getCountries();
    },
    autocompleteInstance() {
      this.autocompleteInstance.addListener('place_changed', this.fillInAddress);
    },
    'fields.email': function () {
      this.validateEmail();
    },
    'fields.country': function (newVal, oldVal) {
      const isInitialCountryChanged = newVal?.id !== oldVal?.id;
      this.$emit('change:country', this.fields.country);
      if (isInitialCountryChanged) {
        this.$emit('addressValidated', false);
        this.addressPopulated = false;
        this.fields.user_address = '';
        this.fields.user_city = '';
        this.fields.user_state = {};
        if (this.isUSA) {
          this.validateAddress();
          this.validateCity();
          this.validateState();
        }
        if (this.isUSA || this.fields.zip?.length > 0) {
          this.validateZip();
        }
        this.validateCountry();
      }
    },
    'fields.zip': function () {
      this.validateZip();
      if (this.isUSA) {
        this.$emit('addressValidated', false);
        this.emitValidateAddressStatus();
      }
    },
    'fields.user_address': function () {
      this.validateAddress();
      this.emitValidateAddressStatus();
    },
    'fields.user_city': function () {
      this.validateCity();
      this.emitValidateAddressStatus();
    },
    'fields.user_state': function () {
      this.validateState();
      this.$emit('addressValidated', false);
      this.emitValidateAddressStatus();
    },
    'fields.first_name': function () {
      this.validateFirstName();
    },
    'fields.last_name': function () {
      this.validateLastName();
    },
    'fields.vat_id': function () {
      this.validateVatId();
    },
    countryUsa() {
      this.fields.user_state = this.getState;
      this.fields.user_city = this.team.city;
      this.fields.user_address = this.team.address;
    },
    currentCountry(newVal) {
      if (newVal?.id) {
        this.countryLoading = false;
      }
    },
  },
  mounted() {
    this.countryLoading = true;
  },
  methods: {
    validateForm() {
      // reset errors
      this.errors = {};

      this.validateEmail();
      this.validateFirstName();
      this.validateLastName();
      this.validateCountry();
      this.validateZip();
      this.validateAddress();
      this.validateCity();
      this.validateState();
      if (this.showABN && this.$refs.vatId?.value) {
        this.validateVatId();
      }
      this.emitValidateStatus();
    },
    emitValidateStatus() {
      this.$nextTick(() => {
        if (this.lastValidationStatus !== this.isFormValid) {
          this.lastValidationStatus = this.isFormValid;
          this.$emit('validatedForm', this.isFormValid);
        }
      });
    },
    validateAddressStatus() {
      this.validateAddress();
      this.validateCity();
      this.validateState();
      if (this.isUSA || this.fields.zip?.length > 0) {
        this.validateZip();
      }
      this.emitValidateAddressStatus();
    },
    emitValidateAddressStatus() {
      this.$nextTick(() => {
        this.$emit('addressValidated', false);
        this.addressLastValidationStatus = this.isAddressValid;
        this.$emit('addressValidated', this.isAddressValid);
      });
    },
    // on Nuxt will use coutries from store, remove this method
    // get current country if team country is not null
    async getCountries() {
      try {
        const { data } = await getCountriesList();
        const currentCountry = data?.items?.find(country => country.id === this.team?.country_id);
        this.currentCountry = currentCountry;
      } catch (error) {
        console.error('get countries error', { error });
      }
    },
    onFocus(key, label) {
      eventTracker(analyticsEvents.inputInteracted, {
        input_placeholder: label,
        section_title: 'invoice details',
        click_source: 'account page',
        vat_vatNumber: this.fields.vat_id,
      });
      this.fieldsPreviousValue[key] = this.fields[key];
    },
    onBlur(key, label) {
      eventTracker(analyticsEvents.inputFilled, {
        input_placeholder: label,
        previous_value: this.fieldsPreviousValue[key],
        input_valid: this.errors[key] === undefined,
        input_value: this.fields[key],
        error_text: this.errors[key],
        section_title: 'personal details',
        click_source: 'account page',
      });
    },
  },
};
</script>

<style scoped>
.field {
    @apply relative my-4;
}
.field.cc-field {
    @apply my-0;
}
.left-half,
.right-half {
    @apply w-full;
}
</style>
